import { startTransition, useActionState, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toaster } from "@/utils/toaster";
import { Button } from "@/components/ui/button";
import { convertToPersianDigits } from "@/components/elements/numbers";
import { type PanelProps } from "../types";

export function SafeCallPanel({ patient, onClose }: PanelProps) {
  const { t } = useTranslation();
  const [timeLeft, setTimeLeft] = useState(0);

  useEffect(() => {
    if (timeLeft === 0) return;
    const timer = setInterval(() => {
      setTimeLeft((t) => t - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [timeLeft]);

  const [, callAction, isCalling] = useActionState(async () => {
    if (!patient.pid) {
      toaster("error", t("error"), {
        description: t("please_select_patient"),
        closeButton: true,
      });
      return null;
    }
    try {
      const res = await fetch(`/api/visitOnline/Calling?vid=${patient.pid}`);
      if (res.ok) {
        const data = await res.json();
        if (data.status === 200) {
          setTimeLeft(30);
          toaster("success", t("safe_call_success"), {
            description: t("safe_call_success_desc"),
            duration: 5000,
            closeButton: true,
          });
        } else {
          toaster("error", t("error_occurred"), { closeButton: true });
        }
      }
    } catch (error) {
      console.error("Safe call error:", error);
      toaster("error", t("error_occurred"), { closeButton: true });
    }
    return null;
  }, null);

  return (
    <div className="flex flex-col min-h-0 gap-4">
      <div className="shrink-0">
        <p className="text-sm text-muted-foreground">{t("safe_call_desc")}</p>
      </div>
      <div className="flex gap-2 w-full shrink-0">
        <Button variant="outline" className="min-w-28" onClick={onClose}>
          {t("cancel")}
        </Button>
        <Button
          disabled={timeLeft > 0 || isCalling}
          className="flex-1"
          onClick={() => startTransition(() => callAction())}
        >
          {isCalling
            ? "..."
            : timeLeft > 0
              ? t("remain_to_retry", {
                  sec: convertToPersianDigits(timeLeft),
                })
              : t("safe_call_submit")}
        </Button>
      </div>
    </div>
  );
}
